import type { ChatHistoryCursor, ChatSessionMessage, ChatSessionSummary } from '~/services/agent-chat.service';
import { CHAT_HISTORY_PAGE_SIZE } from '~/services/agent-chat.service';
import { newChatTurn, type ChatTurn } from '~/utils/agent-chat';

export { CHAT_HISTORY_PAGE_SIZE };

export interface ChatHistoryItem {
  id: string;
  /** Null for a session without a question. The list shows "新しいチャット". */
  title: string | null;
  /** The time of the last message, as an ISO string. */
  date: string;
  hasTicket: boolean;
  ticketUid: string | null;
}

/** The history list. agent-api gives it newest first, and the keyset pages keep that order. */
export function historyItems(sessions: readonly ChatSessionSummary[]): ChatHistoryItem[] {
  return sessions.map(session => ({
    id: session.id,
    title: session.title,
    date: session.last_message_at,
    hasTicket: session.ticket_uid !== null,
    ticketUid: session.ticket_uid,
  }));
}

/** The cursor of the next page, or null when this page was the last one. */
export function historyNextPage(page: readonly ChatSessionSummary[]): ChatHistoryCursor | null {
  const last = page.at(-1);
  if (!last || page.length < CHAT_HISTORY_PAGE_SIZE) return null;
  return { before: last.last_message_at, before_id: last.id };
}

/**
 * The turns of an old session. agent-api gives the question and the final answer of each run, in
 * order. A run without an answer, for example a cancelled one, gives a turn with the error
 * `no_answer`, so the panel offers a retry.
 */
export function turnsFromMessages(messages: readonly ChatSessionMessage[]): ChatTurn[] {
  const turns: ChatTurn[] = [];
  const answer = (turn: ChatTurn, message: ChatSessionMessage) =>
    Object.assign(turn, { text: message.text, messageId: message.id, status: 'completed', final: true, error: null });

  for (const [index, message] of messages.entries()) {
    const last = turns.at(-1);
    if (message.role === 'assistant' && last?.runId === message.run_id && last.status !== 'completed') {
      answer(last, message);
      continue;
    }
    const turn: ChatTurn = {
      ...newChatTurn(`history-${index}`, message.role === 'user' ? message.text : ''),
      runId: message.run_id,
      status: 'failed',
      waiting: false,
      error: { code: 'no_answer', message: '' },
    };
    if (message.role === 'assistant') answer(turn, message);
    turns.push(turn);
  }
  return turns;
}

/** A thumbs down offers the ticket card. A system manager never gets the card. */
export function canOfferTicket(viewer: { isSystemManager: boolean }): boolean {
  return !viewer.isSystemManager;
}
